/**
 * StuckJobsPage — the dispatcher's "Stuck jobs" list (DEC-315 options A and B).
 *
 * GET /v1/jobs/stuck, refreshed every minute. Nothing here changes automatically. Two actions,
 * each for the roles that hold it: Close clock (POST /v1/jobs/:id/dispatcher-close) stops the
 * driver's clock and leaves the job open, so the row stays listed as "Clock stopped"; Cancel
 * job, for the manager tier, cancels a stuck job unless a load was captured (PATCH
 * /v1/jobs/:id/cancel), stopping the clock and notifying the driver. A stuck job WITH a captured
 * load can instead be sent to In Review by the same roles (POST /v1/jobs/:id/force-review,
 * DEC-321), so a reviewer bills what was captured.
 *
 * DEC-329: the company policy (GET /v1/tenants/completion-policy) decides that button: forced
 * now, a request a manager approves, or not offered. Pending requests come from GET
 * /v1/dispatcher-completion-requests; managers see them in a "Waiting for your approval"
 * section above the list, and every viewer sees "Waiting for manager approval" on the row.
 *
 * #42: clicking a row opens the same JobDetailModal the board and queue use.
 */

import * as React from 'react';
import { useApiOpts } from '@lodex-pro/web-shared';
import { useStuckJobs, type StuckJobRow } from '../features/stuck-jobs/use-stuck-jobs';
import { StuckJobsTable } from '../features/stuck-jobs/StuckJobsTable';
import { DispatcherCloseModal } from '../features/stuck-jobs/DispatcherCloseModal';
import { StuckJobCancelModal } from '../features/stuck-jobs/StuckJobCancelModal';
import { ForceReviewModal } from '../features/stuck-jobs/ForceReviewModal';
import { VoidLoadModal } from '../features/stuck-jobs/VoidLoadModal';
import {
  canForceCloseRole,
  canStuckCancelRole,
  canSyncNudgeRole,
} from '../features/stuck-jobs/stuck-jobs-format';
import {
  canApproveCompletionRole,
  forceReviewMode,
  useCompletionPolicy,
  usePendingCompletionRequests,
} from '../features/stuck-jobs/completion-policy';
import { PendingApprovalsSection } from '../features/stuck-jobs/PendingApprovalsSection';
import { JobDetailModal } from '../features/jobs/JobDetailModal';
import { useUserProfile, useUserTimezone } from '../lib/use-user-timezone';

const IN_PROGRESS_HOURS = 24;

export function StuckJobsPage(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { data, isLoading, isError, dataUpdatedAt } = useStuckJobs(apiOpts, IN_PROGRESS_HOURS);
  const timeZone = useUserTimezone();
  const { data: profileData } = useUserProfile();
  const canForceClose = canForceCloseRole(profileData?.profile.role ?? '');
  const canStuckCancel = canStuckCancelRole(profileData?.profile.role ?? '');
  const canSyncNudge = canSyncNudgeRole(profileData?.profile.role ?? '');
  const role = profileData?.profile.role ?? '';
  const { data: policyData } = useCompletionPolicy(apiOpts);
  const mode = forceReviewMode(policyData?.policy ?? null, role);
  const canApprove = canApproveCompletionRole(role);
  const { data: pendingData } = usePendingCompletionRequests(
    apiOpts,
    mode !== 'NONE' || canApprove,
  );
  const pending = React.useMemo(() => pendingData?.requests ?? [], [pendingData]);
  const pendingJobIds = React.useMemo(() => new Set(pending.map((r) => r.jobId)), [pending]);
  const [selectedRow, setSelectedRow] = React.useState<StuckJobRow | null>(null);
  const [cancelRow, setCancelRow] = React.useState<StuckJobRow | null>(null);
  const [forceReviewRow, setForceReviewRow] = React.useState<StuckJobRow | null>(null);
  const [detailJobId, setDetailJobId] = React.useState<string | null>(null);
  // DEC-355 — "Void load and cancel" on a captured-load row.
  const [voidRow, setVoidRow] = React.useState<StuckJobRow | null>(null);

  // The age column is measured against the moment the data arrived, re-read on every refresh,
  // so "31h ago" agrees with the list it sits in.
  const nowMs = React.useMemo(
    () => (dataUpdatedAt > 0 ? Math.max(dataUpdatedAt, Date.now()) : Date.now()),
    [dataUpdatedAt],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border-primary)',
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
        }}
      >
        Jobs with no field activity for {IN_PROGRESS_HOURS}h, or paused longer than your
        tenant&apos;s paused-warning setting. Nothing here changes automatically.
      </div>
      {canApprove ? <PendingApprovalsSection requests={pending} timeZone={timeZone} /> : null}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isError ? (
          <p role="alert" style={{ padding: '16px', color: 'var(--color-danger)' }}>
            Could not load stuck jobs.
          </p>
        ) : isLoading ? (
          <p style={{ padding: '16px', color: 'var(--color-text-tertiary)' }}>Loading…</p>
        ) : (
          <StuckJobsTable
            rows={data?.jobs ?? []}
            timeZone={timeZone}
            canForceClose={canForceClose}
            nowMs={nowMs}
            onCloseClick={setSelectedRow}
            canStuckCancel={canStuckCancel}
            onCancelClick={setCancelRow}
            onForceReviewClick={setForceReviewRow}
            onVoidLoadClick={setVoidRow}
            canSyncNudge={canSyncNudge}
            forceReviewMode={mode}
            pendingJobIds={pendingJobIds}
            onRowClick={(row): void => setDetailJobId(row.jobId)}
          />
        )}
      </div>
      <DispatcherCloseModal
        row={selectedRow}
        open={selectedRow !== null}
        onClose={(): void => setSelectedRow(null)}
        onClosed={(): void => setSelectedRow(null)}
      />
      <StuckJobCancelModal
        row={cancelRow}
        open={cancelRow !== null}
        onClose={(): void => setCancelRow(null)}
        onCancelled={(): void => setCancelRow(null)}
      />
      <ForceReviewModal
        row={forceReviewRow}
        open={forceReviewRow !== null}
        onClose={(): void => setForceReviewRow(null)}
        onSent={(): void => setForceReviewRow(null)}
        asRequest={mode === 'REQUEST'}
      />
      <JobDetailModal jobId={detailJobId} onClose={(): void => setDetailJobId(null)} />
      <VoidLoadModal
        target={voidRow}
        open={voidRow !== null}
        onClose={(): void => setVoidRow(null)}
        onVoided={(): void => setVoidRow(null)}
      />
    </div>
  );
}
